import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useApiOperation } from "src/api/hooks";
import { patchJoinAsync } from "src/api/member/member-api";
import { ConsentAgreement } from "src/api/member/member-types";
import { BaseButton, Spinner } from "src/components";
import { BaseCheckbox } from "src/components/BaseCheckbox";
import { useCourtAuth } from "src/components/court-auth";
import courtAuthStateRepository from "src/components/court-auth/utils/court-auth-state-repository";
import { resolveReturnUri } from "src/components/court-auth/utils/return-uri";
import tokenResponseRepository from "src/components/court-auth/utils/token-response-repository";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { agreementCheckResultState, isJustLoggedInState, pendingJoinEmailState } from "src/recoil/auth/atom";
import { getAppTitle } from "src/utils/app-Info";
import { getPrivacyPolicyUrl } from "../constants";
import { getAgreementMeta } from "./constants";
import { useConsentAgreements } from "./hooks/useConsentAgreements";

/**
 * 약관 동의 화면. 두 종류의 진입을 모두 처리한다
 * - STARTER: 신규 가입 케이스: 동의 시 STARTER => MEMBER 처리 후 약관 기록 (promoteIfStarter)
 * - MEMBER: 개정 약관 등 미동의 약관 재동의
 */
const JoinAgreements = () => {
  const navigate = useNavigate();
  const { security, refreshTokenAsync } = useCourtAuth();
  const memberNo = security.user?.memberNo;
  const setAgreementResult = useSetRecoilState(agreementCheckResultState);
  const setIsJustLoggedIn = useSetRecoilState(isJustLoggedInState);
  const pendingJoinEmail = useRecoilValue(pendingJoinEmailState);
  const { fetchUngrantedAgreements, submitAllConsents, isSubmitting } = useConsentAgreements();
  const { executeAsync: patchJoin } = useApiOperation(patchJoinAsync);

  // 미동의 약관이 없거나 동의 완료 시 넘기기
  const markAgreementResolved = useCallback(() => {
    if (memberNo) setAgreementResult({ memberNo, required: false });
  }, [memberNo, setAgreementResult]);

  // STARTER는 약관 기록 전에 먼저 MEMBER로 처리
  const promoteIfStarter = useCallback(async (): Promise<boolean> => {
    if (!security.user?.roles.includes("ROLE_STARTER")) return true;
    const email = security.user?.email?.value || pendingJoinEmail || undefined;
    const { data } = await patchJoin({ email, marketingConsent: false });
    if (!data?.data?.member) return false;
    await refreshTokenAsync();
    return true;
  }, [security, patchJoin, refreshTokenAsync, pendingJoinEmail]);

  const [agreements, setAgreements] = useState<ConsentAgreement[] | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const fetchedRef = useRef(false);

  const agreementMeta = useMemo(() => getAgreementMeta(), []);
  const privacyPolicyUrl = useMemo(() => getPrivacyPolicyUrl(), []);

  // 인증 후 복귀 경로 (인증 흐름 경로면 메인으로 대체된다)
  const returnUri = resolveReturnUri(courtAuthStateRepository.load()?.returnTo?.latest?.uri);

  // 약관 페이지 도달 = 온보딩 첫 화면 진입 완료 → 이후 약관 미동의로 이탈하면 비로그인(로그인 프로세스)으로 처리
  useEffect(() => {
    setIsJustLoggedIn(false);
  }, [setIsJustLoggedIn]);

  // 미동의 약관 조회 (마운트 시 1회)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      const list = await fetchUngrantedAgreements();
      if (list.length === 0) {
        if (await promoteIfStarter()) {
          markAgreementResolved();
          tokenResponseRepository.commit();
          navigate(returnUri);
        }
        return;
      }
      setAgreements(list);
    })();
  }, [fetchUngrantedAgreements, navigate, returnUri, markAgreementResolved, promoteIfStarter]);

  // 필수 약관 전체 체크 여부
  const allChecked = useMemo(() => {
    return !!agreements && agreements.length > 0 && agreements.every((agreement) => checked[agreement.code]);
  }, [agreements, checked]);

  // 전체 동의 토글
  const onToggleAll = useCallback(
    (value: boolean) => {
      if (!agreements) return;
      const next: Record<string, boolean> = {};
      agreements.forEach((agreement) => {
        next[agreement.code] = value;
      });
      setChecked(next);
    },
    [agreements],
  );

  // 개별 약관 토글
  const onToggleOne = useCallback((code: string, value: boolean) => {
    setChecked((prev) => ({ ...prev, [code]: value }));
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!agreements || !allChecked || isSubmitting) return;

      if (!(await promoteIfStarter())) return;
      await submitAllConsents(agreements);
      markAgreementResolved();
      tokenResponseRepository.commit();
      navigate(returnUri);
    },
    [agreements, allChecked, isSubmitting, submitAllConsents, markAgreementResolved, navigate, returnUri, promoteIfStarter],
  );

  return (
    <>
      <Header headerType={"NONE"} rightDisabled />
      <div className="my-page">
        <MetaTag title={`약관 및 개인정보 동의 | ${getAppTitle()}`} />

        <article className="container join-agreements">
          <form onSubmit={onSubmit}>
            <div className="title-wrap">
              <h5>{"서비스 이용을 위해\n아래 내용을 확인해 주세요."}</h5>
            </div>

            {/* 조회 중 */}
            {agreements === null && (
              <div className="spinner-container">
                <Spinner size="page" />
              </div>
            )}

            {/* 미동의 약관 목록 */}
            {agreements !== null && agreements.length > 0 && (
              <>
                <div className="agreement-list">
                  {/* 전체 동의 */}
                  <BaseCheckbox id="agreement-all" name="agreement-all" className="agreement-all" checked={allChecked} onChange={onToggleAll}>
                    <span className="ml8 agreement-all-label">아래 항목에 모두 동의합니다.</span>
                  </BaseCheckbox>

                  {/* 약관 동의 항목 */}
                  {agreements.map((agreement) => {
                    const meta = agreementMeta[agreement.code];
                    const label = agreement.activeRevision?.title ?? meta?.label ?? agreement.code;

                    return (
                      <BaseCheckbox
                        key={agreement.code}
                        id={`agreement-${agreement.code}`}
                        name={`agreement-${agreement.code}`}
                        className="agreement-item"
                        checked={!!checked[agreement.code]}
                        onChange={(value) => onToggleOne(agreement.code, value)}
                      >
                        {meta?.url ? (
                          <span className="ml8 agreement-item-label">
                            [필수]{" "}
                            <a target="_blank" rel="noreferrer noopener" href={meta.url}>
                              {label}
                            </a>
                          </span>
                        ) : (
                          <span className="ml8 agreement-item-label">[필수] {label}</span>
                        )}
                      </BaseCheckbox>
                    );
                  })}
                </div>

                {/* 안내 문구 */}
                <p className="agreement-guide">
                  자세한 내용은{" "}
                  <a target="_blank" rel="noreferrer noopener" href={privacyPolicyUrl}>
                    개인정보 처리방침
                  </a>
                  에서 확인하실 수 있습니다.{"\n"}필수 항목에 동의하지 않으실 경우 서비스 이용이 제한됩니다.
                </p>

                <BaseButton type="submit" className="login-btn" title="동의하고 시작하기" disabled={!allChecked} isLoading={isSubmitting} />
              </>
            )}
          </form>
        </article>
      </div>
    </>
  );
};

export default JoinAgreements;
